import { toast } from 'sonner'
import useCallStore from '@/store/call-store'
import { checkIsAudioAccessible } from '@/utils/browser'
import { sleep } from '@/utils/helper.js'
import callEventLog, { getSessionLogData } from '@/utils/call_event_log.js'
import { CallType } from '@/constants/dial-call'

export const SessionStatuses = Object.freeze({
  NULL: 0,
  INVITE_SENT: 1,
  _1XX_RECEIVED: 2,
  INVITE_RECEIVED: 3,
  WAITING_FOR_ANSWER: 4,
  ANSWERED: 5,
  WAITING_FOR_ACK: 6,
  CANCELED: 7,
  TERMINATED: 8,
  CONFIRMED: 9,
})

let ua = null
let sessions = []

let sessionCount = 0
let uaRestartCount = 0
let prevNoSessionCount = 0
let prevSipDisconnectedSessionCount = 0

const getSessionCallId = (session) => session?._request?.call_id

const getLatestSession = () => sessions[sessions.length - 1]

const addSession = (newSession) => {
  const newSessionCallId = getSessionCallId(newSession)
  if (!newSessionCallId) return

  sessions = sessions.filter((s) => getSessionCallId(s) !== newSessionCallId)

  sessions.push(newSession)
  if (sessions.length > 3) {
    sessions.shift()
  }
}

const iceServers = [
  {
    urls: [
      'stun:stun.l.google.com:19302',
      'stun:global.stun.twilio.com:3478',
      'stun:stun.cloudflare.com:3478',
    ],
  },
]

const waitForJsSIP = async () => {
  let attempts = 0
  while (typeof window.JsSIP === 'undefined' && attempts < 3) {
    await sleep(200)
    attempts++
  }
  return typeof window.JsSIP !== 'undefined'
}

export const initUa = async () => {
  const isAudioAccessible = await checkIsAudioAccessible()
  if (!isAudioAccessible) return

  // Ensure window.JsSIP is available
  const jsipAvailable = await waitForJsSIP()
  if (!jsipAvailable) {
    toast.error('Không thể tải được JsSIP, vui lòng reload lại trang!')
    return
  }

  const callee = useCallStore.getState().callee
  let uri = callee.sipGatewayUrl
  const password = callee.sipPass
  const agentId = callee.agentId

  const [primaryUri, secondaryUri] = uri.split(',')
  const socketList = [
    {
      socket: new window.JsSIP.WebSocketInterface(`wss://${primaryUri}`),
      weight: 1,
    },
  ]
  if (secondaryUri) {
    socketList.push({
      socket: new window.JsSIP.WebSocketInterface(`wss://${secondaryUri}`),
      weight: 0,
    })
  }
  const webrtcSocketUrl =
    import.meta.env.VITE_WEBRTC_SOCKET_URL || 'webrtc.zenify.vn'

  const configuration = {
    sockets: socketList,
    uri: `sip:${agentId}@${webrtcSocketUrl.replace('https://', '')}:8443`,
    password,
    register: true,
    register_expires: 120,
    session_timers: false,
    connection_recovery_min_interval: 2,
    connection_recovery_max_interval: 10,
  }

  ua = new window.JsSIP.UA(configuration)
  ua.on('registered', () => {
    callEventLog.addSipEvent('Registered', null)
  })
  ua.on('unregistered', () => {
    callEventLog.addSipEvent('Unregistered', null)
  })
  ua.on('newRTCSession', (e) => {
    console.log('newRTCSession', e)
    const currentSession = e.session
    const callId = getSessionCallId(currentSession)
    const eventData = {
      event: 'newRTCSession',
      session: getSessionLogData(currentSession),
    }
    callEventLog.addEventByCallId(callId, eventData)

    currentSession.on('accepted', () => {
      console.log('Call accepted')
      const callId = getSessionCallId(currentSession)
      const eventData = {
        event: 'session accepted',
        session: getSessionLogData(currentSession),
      }

      callEventLog.addEventByCallId(callId, eventData)

      const remoteReceiver = currentSession.connection.getReceivers()[0]
      const audioTrack = new MediaStream([remoteReceiver.track])
      useCallStore.getState().setAudioTrack(audioTrack)
    })
    currentSession.on('progress', () => {
      console.log('ua progress 1XX SIP response')
      const callId = getSessionCallId(currentSession)
      const eventData = {
        event: 'session progress',
        session: getSessionLogData(currentSession),
      }
      callEventLog.addEventByCallId(callId, eventData)
    })
    currentSession.on('ended', (e) => {
      console.log('session ended', e)
      const callId = getSessionCallId(currentSession)
      const eventData = {
        event: 'session ended',
        data: {
          cause: e?.cause,
          originator: e?.originator,
          status_code: e?.message?.status_code,
          reason_phrase: e?.message?.reason_phrase,
        },
        session: getSessionLogData(currentSession),
      }
      callEventLog.addEventByCallId(callId, eventData)

      const callStore = useCallStore.getState()
      callStore.setAudioTrack(null)
    })
    currentSession.on('failed', async (e) => {
      console.log('session failed', e)
      const callId = getSessionCallId(currentSession)
      const cause = e?.cause
      const originator = e?.originator
      const eventData = {
        event: 'session failed',
        data: {
          cause,
          originator,
          status_code: e?.message?.status_code,
          reason_phrase: e?.message?.reason_phrase,
        },
        session: getSessionLogData(currentSession),
      }
      callEventLog.addEventByCallId(callId, eventData)

      const callStore = useCallStore.getState()
      callStore.setAudioTrack(null)

      const callType = callStore.callInfo?.callType
      if (
        callType === CallType.INCOMING &&
        cause === 'Canceled' &&
        originator === 'remote'
      ) {
        // The call is incoming and the ring time has expired
        // This is not a failure, just a timeout
        return
      }

      const lastHangupClickAt = getLastHangupClickAt()
      const now = new Date().getTime()
      if (!lastHangupClickAt || now - lastHangupClickAt > 1000) {
        callEventLog.sendEventByCallId('event_session_failed', callId)
      }
    })

    sessionCount += 1
    addSession(currentSession)
  })
  ua.on('connected', (data) => {
    console.log('SIP connected')
    callEventLog.addSipEvent('Connected', null)
    useCallStore.getState().setIsSipConnected(true)

    data.socket.ondisconnect = () => {
      console.log('SIP socket disconnected')
      const eventData = {
        error: data?.error,
        code: data?.code,
        reason: data?.reason,
        sessionCount,
        uaRestartCount,
        prevSipDisconnectedSessionCount,
      }
      callEventLog.addSipEvent('Socket Disconnected', eventData)
      prevSipDisconnectedSessionCount = sessionCount
      useCallStore.getState().setIsSipConnected(false)
    }
  })
  ua.on('disconnected', (data) => {
    console.log('SIP disconnected', data)
    const eventData = {
      error: data?.error,
      code: data?.code,
      reason: data?.reason,
      sessionCount,
      uaRestartCount,
      prevSipDisconnectedSessionCount,
    }
    callEventLog.addSipEvent('Disconnected', eventData)
    prevSipDisconnectedSessionCount = sessionCount
    useCallStore.getState().setIsSipConnected(false)
  })
  ua.on('registrationFailed', (data) => {
    console.log('SIP registrationFailed')
    const eventData = {
      cause: data?.cause,
      status_code: data?.response?.status_code,
      reason_phrase: data?.response?.reason_phrase,
    }
    callEventLog.addSipEvent('Registration Failed', eventData)
    useCallStore.getState().setIsSipConnected(false)
    restartUa('registrationFailed')
  })
  callEventLog.addSipEvent('Initialized', configuration)
}

export const startUa = async (reason) => {
  if (!ua) {
    await initUa()
  }
  if (!ua) {
    callEventLog.addSipEvent('Initialize Failed', { reason })
  }

  ua.start()

  callEventLog.addSipEvent('UA started', { reason })
}

export const stopUa = (reason) => {
  if (!ua) return

  ua.stop()
  callEventLog.addSipEvent('UA stopped', { reason })
}

export const restartUa = async (reason) => {
  console.log(`Restarting SIP...`)
  callEventLog.addSipEvent('Restart', { reason, uaRestartCount })
  uaRestartCount += 1
  stopUa('restart')
  await sleep(500)
  startUa('restart')
}

export const clearUa = () => {
  stopUa('clear')
  ua = null
  sessions = []
  sessionCount = 0
  uaRestartCount = 0
  prevNoSessionCount = 0
  prevSipDisconnectedSessionCount = 0
  callEventLog.clear()
}

export async function waitForSession(callId) {
  const startTime = Date.now()
  while (Date.now() - startTime < 500) {
    const session = getLatestSession()
    if (session && getSessionCallId(session) === callId) {
      return session
    }
    await sleep(200) // sleep trả về một promise resolve sau interval ms
  }
  return null
}

export const answerCall = async (callId) => {
  const session = await waitForSession(callId)
  if (!session || session._status !== SessionStatuses.WAITING_FOR_ANSWER) {
    const currentCallId = useCallStore.getState().callInfo?.callId
    if (currentCallId === callId) {
      toast.error('Kết nối bị gián đoạn, vui lòng thử lại sau vài giây!')
      useCallStore.getState().resetCall()
    } else {
      callEventLog.addEventByCallId(callId, {
        event: '[answerCall] Wrong Call Id',
        currentCallId,
        session: getSessionLogData(session),
        sessionCount: sessionCount,
        uaRestartCount: uaRestartCount,
        prevNoSessionCount: prevNoSessionCount,
        sessions: sessions.map(getSessionLogData),
      })
      setTimeout(() => {
        callEventLog.sendEventByCallId('answer_call_wrong_call_id', callId)
      }, 6000)
      prevNoSessionCount = sessionCount
      return
    }
  }

  try {
    session.answer({
      sessionTimersExpires: 18000,
      mediaConstraints: { audio: true, video: false },
      pcConfig: { rtcpMuxPolicy: 'require', iceServers },
      rtcOfferConstraints: {
        offerToReceiveAudio: 1,
        offerToReceiveVideo: 0,
      },
    })
    callEventLog.addEventByCallId(callId, {
      event: '[answerCall] Session Answered',
      session: getSessionLogData(session),
    })
  } catch (e) {
    console.error(e)
    toast.error('Có lỗi xảy ra khi kết nối cuộc gọi')
    callEventLog.addEventByCallId(callId, {
      event: '[answerCall] Answers Failed',
      session: getSessionLogData(session),
      sessions: sessions.map(getSessionLogData),
    })
    setTimeout(() => {
      callEventLog.sendEventByCallId('answer_call_answers_failed', callId)
    }, 6000)
  }
}

export const setLastHangupClickAt = () => {
  localStorage.setItem('lastHangupClickAt', new Date().getTime().toString())
}

export const getLastHangupClickAt = () => {
  const lastHangupClickAt = localStorage.getItem('lastHangupClickAt')
  return lastHangupClickAt ? Number(lastHangupClickAt) : null
}
